import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Users, X, UserPlus, Tag, Edit2, Trash2, User, Phone, Building2, UsersRound, Shield, Bot, Save, Briefcase, Sparkles, FileText, Mail, Search, ChevronLeft, ChevronRight, BarChart3, Calendar, AlertCircle } from 'lucide-react';



const getAvatarGradient = (name) => {
  if (!name) return 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)';
  const gradients = [
    'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', // Indigo/Violet
    'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', // Pink
    'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', // Blue
    'linear-gradient(135deg, #10b981 0%, #047857 100%)', // Emerald
    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Amber
    'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', // Purple
    'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', // Cyan
    'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)', // Rose
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return gradients[sum % gradients.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
};

function AdminPanel({ user, workspaces = [], onRefreshWorkspaces }) {
  const [searchParams] = useSearchParams();
  const activeSection = searchParams.get('tab') || 'workspaces';


  
  // Workspaces management state
  const [localWorkspaces, setLocalWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [workspaceForm, setWorkspaceForm] = useState({ name: '', description: '', agency_id: '', owner_contact_id: '' });
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  // Carlos Skills state
  const [carlosSkills, setCarlosSkills] = useState([]);
  const [editingSkill, setEditingSkill] = useState(null);
  const [skillForm, setSkillForm] = useState({ name: '', description: '', icon: 'file', questions: '', output_template: '', is_active: true });
  const [showSkillForm, setShowSkillForm] = useState(false);
  
  // Agencies, People Clients, People Teams state (for contacts)
  const [agencies, setAgencies] = useState([]);
  const [peopleClients, setPeopleClients] = useState([]);
  const [peopleTeams, setPeopleTeams] = useState([]);
  const [agencyName, setAgencyName] = useState('');
  const [agencyDescription, setAgencyDescription] = useState('');
  const [editingAgency, setEditingAgency] = useState(null);
  const [peopleClientName, setPeopleClientName] = useState('');
  const [peopleClientDescription, setPeopleClientDescription] = useState('');
  const [editingPeopleClient, setEditingPeopleClient] = useState(null);
  const [peopleTeamName, setPeopleTeamName] = useState('');
  const [peopleTeamDescription, setPeopleTeamDescription] = useState('');
  const [editingPeopleTeam, setEditingPeopleTeam] = useState(null);
  
  // Project Access state
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [roleColor, setRoleColor] = useState('#000000');
  const [editingRole, setEditingRole] = useState(null);
  
  // People/Contacts state
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '' });

  // People Search & Pagination
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peoplePage, setPeoplePage] = useState(1);
  const peoplePerPage = 10;

  useEffect(() => {
    setPeoplePage(1);
  }, [peopleSearch]);

  const filteredContacts = useMemo(() => {
    if (!peopleSearch) return contacts;
    const lower = peopleSearch.toLowerCase();
    return contacts.filter(c => 
      c.name?.toLowerCase().includes(lower) ||
      c.email?.toLowerCase().includes(lower) ||
      c.number?.toLowerCase().includes(lower) ||
      c.role_name?.toLowerCase().includes(lower) ||
      c.source?.toLowerCase().includes(lower)
    );
  }, [contacts, peopleSearch]);

  const paginatedContacts = useMemo(() => {
    const start = (peoplePage - 1) * peoplePerPage;
    return filteredContacts.slice(start, start + peoplePerPage);
  }, [filteredContacts, peoplePage]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / peoplePerPage));

  // Agent configurations state
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({ name: '', role: '', system_prompt: '', greeting: '', color: '#667eea', suggested_prompts: [], language: 'en' });
  const [savingAgent, setSavingAgent] = useState(false);

  // Daily digest state
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState(null);

  // Office BI Report state
  const [officeBiData, setOfficeBiData] = useState([]);
  const [loadingOfficeBi, setLoadingOfficeBi] = useState(false);
  const [officeBiError, setOfficeBiError] = useState(null);
  
  // BI Filters
  const [biDateRange, setBiDateRange] = useState('this_month'); // 'this_month' | 'last_30_days' | 'this_quarter' | 'this_year' | 'custom'
  const [biCustomStart, setBiCustomStart] = useState('');
  const [biCustomEnd, setBiCustomEnd] = useState('');
  const [biSelectedUser, setBiSelectedUser] = useState('all');
  const [biSelectedTeam, setBiSelectedTeam] = useState('all');
  const [biSelectedClient, setBiSelectedClient] = useState('all');
  const [biSelectedAgency, setBiSelectedAgency] = useState('all');
  
  // BI Sub-tab
  const [biSubTab, setBiSubTab] = useState('overview'); // 'overview' | 'people'

  const loadOfficeBiData = useCallback(async () => {
    setLoadingOfficeBi(true);
    setOfficeBiError(null);
    try {
      const res = await axios.get('/api/admin/attendance/report');
      setOfficeBiData(res.data);
    } catch (err) {
      console.error('Error loading Office BI report:', err);
      setOfficeBiError(err.response?.data?.error || 'Failed to load report data');
    } finally {
      setLoadingOfficeBi(false);
    }
  }, []);

  const handleSendDigest = async () => {
    if (sendingDigest) return;
    if (!window.confirm('Send the daily planner digest to all stakeholders and team leads now?')) return;
    setSendingDigest(true);
    setDigestResult(null);
    try {
      const res = await axios.post('/api/admin/send-daily-digest');
      setDigestResult({ success: true, ...res.data });
      setTimeout(() => setDigestResult(null), 8000);
    } catch (err) {
      setDigestResult({ success: false, error: err.response?.data?.error || err.message });
      setTimeout(() => setDigestResult(null), 8000);
    } finally {
      setSendingDigest(false);
    }
  };

  const loadWorkspacesLocal = useCallback(async () => {
    try {
      const response = await axios.get('/api/workspaces');
      setLocalWorkspaces(response.data);
      if (onRefreshWorkspaces) {
        onRefreshWorkspaces();
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
  }, [onRefreshWorkspaces]);

  const loadProjects = useCallback(async () => {
    const response = await axios.get('/api/admin/projects');
    setProjects(response.data);
  }, []);

  const loadUsers = useCallback(async () => {
    const response = await axios.get('/api/admin/users');
    setUsers(response.data);
  }, []);

  const loadProjectMembers = useCallback(async (projectId) => {
    const response = await axios.get(`/api/admin/projects/${projectId}/members`);
    setProjectMembers(response.data);
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      const response = await axios.get('/api/contact-roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }, []);

  const loadContacts = useCallback(async () => {
    setLoadingContacts(true);
    try {
      const response = await axios.get('/api/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  const loadAgentConfigs = useCallback(async () => {
    try {
      const response = await axios.get('/api/agent-configs');
      setAgentConfigs(response.data);
    } catch (error) {
      console.error('Error loading agent configs:', error);
    }
  }, []);

  const loadCarlosSkills = useCallback(async () => {
    try {
      const response = await axios.get('/api/carlos-skills');
      setCarlosSkills(response.data);
    } catch (error) {
      console.error('Error loading Carlos skills:', error);
    }
  }, []);

  const loadAgencies = useCallback(async () => {
    try {
      const response = await axios.get('/api/agencies');
      setAgencies(response.data);
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  }, []);

  const loadPeopleClients = useCallback(async () => {
    try {
      const response = await axios.get('/api/people-clients');
      setPeopleClients(response.data);
    } catch (error) {
      console.error('Error loading people clients:', error);
    }
  }, []);

  const loadPeopleTeams = useCallback(async () => {
    try {
      const response = await axios.get('/api/people-teams');
      setPeopleTeams(response.data);
    } catch (error) {
      console.error('Error loading people teams:', error);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadUsers();
    loadRoles();
    loadContacts();
    loadAgentConfigs();
    loadCarlosSkills();
    loadAgencies();
    loadPeopleClients();
    loadPeopleTeams();
    loadWorkspacesLocal();
  }, [loadProjects, loadUsers, loadRoles, loadContacts, loadAgentConfigs, loadCarlosSkills, loadAgencies, loadPeopleClients, loadPeopleTeams, loadWorkspacesLocal]);

  useEffect(() => {
    if (selectedProject) {
      loadProjectMembers(selectedProject.id);
    }
  }, [selectedProject, loadProjectMembers]);

  const loadWorkspaceMembers = useCallback(async (workspaceId) => {
    try {
      const response = await axios.get(`/api/workspaces/${workspaceId}/members`);
      setWorkspaceMembers(response.data);
    } catch (error) {
      console.error('Error loading workspace members:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      loadWorkspaceMembers(selectedWorkspace.id);
    }
  }, [selectedWorkspace, loadWorkspaceMembers]);

  useEffect(() => {
    if (activeSection === 'office_bi') {
      loadOfficeBiData();
    }
  }, [activeSection, loadOfficeBiData]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedUserId || !selectedProject) return;
    try {
      await axios.post(`/api/admin/projects/${selectedProject.id}/members`, {
        user_id: parseInt(selectedUserId)
      });
      setSelectedUserId('');
      loadProjectMembers(selectedProject.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this user from the project?')) return;
    try {
      await axios.delete(`/api/admin/projects/${selectedProject.id}/members/${userId}`);
      loadProjectMembers(selectedProject.id);
    } catch (error) {
      alert('Failed to remove member');
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) return;
    try {
      if (editingRole) {
        await axios.put(`/api/contact-roles/${editingRole.id}`, {
          name: roleName.trim(),
          description: roleDescription.trim(),
          color: roleColor
        });
      } else {
        await axios.post('/api/contact-roles', {
          name: roleName.trim(),
          description: roleDescription.trim(),
          color: roleColor
        });
      }
      setRoleName('');
      setRoleDescription('');
      setRoleColor('#000000');
      setEditingRole(null);
      loadRoles();
    } catch (error) {
      alert('Failed to save role');
    }
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setRoleColor(role.color || '#000000');
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this role? Contacts with this role will have their role removed.')) return;
    try {
      await axios.delete(`/api/contact-roles/${roleId}`);
      loadRoles();
    } catch (error) {
      alert('Failed to delete role');
    }
  };

  const cancelEditRole = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setRoleColor('#000000');
  };

  // Contact handlers
  const handleSaveContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.number) {
      alert('Please fill in name and number');
      return;
    }
    try {
      const payload = { 
        ...contactForm, 
        role_id: contactForm.role_id || null,
        agency_id: contactForm.agency_id || null,
        people_client_id: contactForm.people_client_id || null,
        people_team_id: contactForm.people_team_id || null
      };
      if (editingContact) {
        await axios.put(`/api/contacts/${editingContact.id}`, payload);
      } else {
        await axios.post('/api/contacts', payload);
      }
      setContactForm({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '' });
      setEditingContact(null);
      setShowContactForm(false);
      loadContacts();
    } catch (error) {
      alert('Failed to save contact');
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name,
      number: contact.number || '',
      email: contact.email || '',
      role_id: contact.role_id || '',
      agency_id: contact.agency_id || '',
      people_client_id: contact.people_client_id || '',
      people_team_id: contact.people_team_id || ''
    });
    setShowContactForm(true);
  };

  const handleDeleteContact = async (contactId, contactName) => {
    if (!window.confirm(`Delete contact "${contactName}"?`)) return;
    try {
      await axios.delete(`/api/contacts/${contactId}`);
      loadContacts();
    } catch (error) {
      alert('Failed to delete contact');
    }
  };

  const cancelContactForm = () => {
    setShowContactForm(false);
    setEditingContact(null);
    setContactForm({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '' });
  };

  // Agency handlers
  const handleSaveAgency = async (e) => {
    e.preventDefault();
    if (!agencyName.trim()) return;
    try {
      if (editingAgency) {
        await axios.put(`/api/agencies/${editingAgency.id}`, {
          name: agencyName.trim(),
          description: agencyDescription.trim()
        });
      } else {
        await axios.post('/api/agencies', {
          name: agencyName.trim(),
          description: agencyDescription.trim()
        });
      }
      setAgencyName('');
      setAgencyDescription('');
      setEditingAgency(null);
      loadAgencies();
    } catch (error) {
      alert('Failed to save agency');
    }
  };

  const handleEditAgency = (agency) => {
    setEditingAgency(agency);
    setAgencyName(agency.name);
    setAgencyDescription(agency.description || '');
  };

  const handleDeleteAgency = async (agencyId) => {
    if (!window.confirm('Delete this agency?')) return;
    try {
      await axios.delete(`/api/agencies/${agencyId}`);
      loadAgencies();
    } catch (error) {
      alert('Failed to delete agency');
    }
  };

  const cancelEditAgency = () => {
    setEditingAgency(null);
    setAgencyName('');
    setAgencyDescription('');
  };

  // People Client handlers
  const handleSavePeopleClient = async (e) => {
    e.preventDefault();
    if (!peopleClientName.trim()) return;
    try {
      if (editingPeopleClient) {
        await axios.put(`/api/people-clients/${editingPeopleClient.id}`, {
          name: peopleClientName.trim(),
          description: peopleClientDescription.trim()
        });
      } else {
        await axios.post('/api/people-clients', {
          name: peopleClientName.trim(),
          description: peopleClientDescription.trim()
        });
      }
      setPeopleClientName('');
      setPeopleClientDescription('');
      setEditingPeopleClient(null);
      loadPeopleClients();
    } catch (error) {
      alert('Failed to save client');
    }
  };

  const handleEditPeopleClient = (client) => {
    setEditingPeopleClient(client);
    setPeopleClientName(client.name);
    setPeopleClientDescription(client.description || '');
  };

  const handleDeletePeopleClient = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await axios.delete(`/api/people-clients/${clientId}`);
      loadPeopleClients();
    } catch (error) {
      alert('Failed to delete client');
    }
  };

  const cancelEditPeopleClient = () => {
    setEditingPeopleClient(null);
    setPeopleClientName('');
    setPeopleClientDescription('');
  };

  // People Team handlers
  const handleSavePeopleTeam = async (e) => {
    e.preventDefault();
    if (!peopleTeamName.trim()) return;
    try {
      if (editingPeopleTeam) {
        await axios.put(`/api/people-teams/${editingPeopleTeam.id}`, {
          name: peopleTeamName.trim(),
          description: peopleTeamDescription.trim()
        });
      } else {
        await axios.post('/api/people-teams', {
          name: peopleTeamName.trim(),
          description: peopleTeamDescription.trim()
        });
      }
      setPeopleTeamName('');
      setPeopleTeamDescription('');
      setEditingPeopleTeam(null);
      loadPeopleTeams();
    } catch (error) {
      alert('Failed to save team');
    }
  };

  const handleEditPeopleTeam = (team) => {
    setEditingPeopleTeam(team);
    setPeopleTeamName(team.name);
    setPeopleTeamDescription(team.description || '');
  };

  const handleDeletePeopleTeam = async (teamId) => {
    if (!window.confirm('Delete this team?')) return;
    try {
      await axios.delete(`/api/people-teams/${teamId}`);
      loadPeopleTeams();
    } catch (error) {
      alert('Failed to delete team');
    }
  };

  const cancelEditPeopleTeam = () => {
    setEditingPeopleTeam(null);
    setPeopleTeamName('');
    setPeopleTeamDescription('');
  };

  // Agent handlers
  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    // Parse suggested_prompts from JSON string if needed
    let prompts = [];
    if (agent.suggested_prompts) {
      try {
        prompts = typeof agent.suggested_prompts === 'string' 
          ? JSON.parse(agent.suggested_prompts) 
          : agent.suggested_prompts;
      } catch (e) {
        prompts = [];
      }
    }
    setAgentForm({
      name: agent.name,
      role: agent.role,
      system_prompt: agent.system_prompt,
      greeting: agent.greeting || '',
      color: agent.color || '#667eea',
      suggested_prompts: prompts,
      language: agent.language || 'en'
    });
  };

  const handleSaveAgent = async () => {
    if (!editingAgent) return;
    setSavingAgent(true);
    try {
      await axios.put(`/api/agent-configs/${editingAgent.agent_id}`, {
        ...agentForm,
        is_active: editingAgent.is_active
      });
      loadAgentConfigs();
      setEditingAgent(null);
      setAgentForm({ name: '', role: '', system_prompt: '', greeting: '', color: '#667eea', suggested_prompts: [], language: 'en' });
    } catch (error) {
      alert('Failed to save agent configuration');
    } finally {
      setSavingAgent(false);
    }
  };

  const cancelAgentForm = () => {
    setEditingAgent(null);
    setAgentForm({ name: '', role: '', system_prompt: '', greeting: '', color: '#667eea', suggested_prompts: [], language: 'en' });
  };

  const handleAddPrompt = () => {
    setAgentForm({
      ...agentForm,
      suggested_prompts: [...agentForm.suggested_prompts, '']
    });
  };

  const handleRemovePrompt = (index) => {
    setAgentForm({
      ...agentForm,
      suggested_prompts: agentForm.suggested_prompts.filter((_, i) => i !== index)
    });
  };

  const handlePromptChange = (index, value) => {
    const newPrompts = [...agentForm.suggested_prompts];
    newPrompts[index] = value;
    setAgentForm({ ...agentForm, suggested_prompts: newPrompts });
  };

  const availableUsers = users
    .filter((u) => !projectMembers.some((m) => m.user_id === u.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const handleTogglePlannerAccess = async (userId, currentValue) => {
    try {
      await axios.put(`/api/admin/users/${userId}/planner-access`, {
        can_edit_planner: !currentValue
      });
      loadUsers();
    } catch (error) {
      alert('Failed to update planner access');
    }
  };

  const handleTogglePeopleAccess = async (userId, currentValue) => {
    try {
      await axios.put(`/api/admin/users/${userId}/people-access`, {
        can_view_people: !currentValue
      });
      loadUsers();
    } catch (error) {
      alert('Failed to update people access');
    }
  };

  const handleToggleAttendanceAccess = async (userId, currentValue) => {
    try {
      await axios.put(`/api/admin/users/${userId}/attendance-access`, {
        can_view_all_attendance: !currentValue
      });
      loadUsers();
    } catch (error) {
      alert('Failed to update attendance access');
    }
  };

  const handleToggleAdminStatus = async (userId, currentValue) => {
    if (userId === user?.id) {
      alert("You cannot change your own admin status.");
      return;
    }
    try {
      await axios.put(`/api/admin/users/${userId}/admin-status`, {
        is_admin: !currentValue
      });
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update admin status');
    }
  };

  // Carlos Skills handlers
  const handleSaveSkill = async (e) => {
    e.preventDefault();
    if (!skillForm.name || !skillForm.questions || !skillForm.output_template) {
      alert('Name, Questions, and Output Template are required');
      return;
    }
    
    try {
      if (editingSkill) {
        await axios.put(`/api/carlos-skills/${editingSkill.id}`, skillForm);
      } else {
        await axios.post('/api/carlos-skills', skillForm);
      }
      loadCarlosSkills();
      setShowSkillForm(false);
      setEditingSkill(null);
      setSkillForm({ name: '', description: '', icon: 'file', questions: '', output_template: '', is_active: true });
    } catch (error) {
      alert('Failed to save skill');
    }
  };

  const handleEditSkill = (skill) => {
    setEditingSkill(skill);
    setSkillForm({
      name: skill.name,
      description: skill.description || '',
      icon: skill.icon || 'file',
      questions: skill.questions,
      output_template: skill.output_template,
      is_active: skill.is_active
    });
    setShowSkillForm(true);
  };

  const handleDeleteSkill = async (skillId) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;
    try {
      await axios.delete(`/api/carlos-skills/${skillId}`);
      loadCarlosSkills();
    } catch (error) {
      alert('Failed to delete skill');
    }
  };

  const handleToggleSkillActive = async (skill) => {
    try {
      await axios.put(`/api/carlos-skills/${skill.id}`, {
        ...skill,
        is_active: !skill.is_active
      });
      loadCarlosSkills();
    } catch (error) {
      alert('Failed to toggle skill status');
    }
  };

  // Render section components
  const renderSkillsSection = () => (
    <div className="card">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">Carlos Mario Skills</h3>
          <p className="admin-section-description">Configure skills that Carlos Mario can use to guide conversations.</p>
        </div>
        {!showSkillForm && (
          <button onClick={() => { setShowSkillForm(true); setEditingSkill(null); setSkillForm({ name: '', description: '', icon: 'file', questions: '', output_template: '', is_active: true }); }} className="btn btn-primary">
            <Plus size={16} /> Add Skill
          </button>
        )}
      </div>

      {showSkillForm && (
        <div style={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
          borderRadius: '16px', 
          padding: '24px', 
          marginBottom: '1.5rem',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Sparkles size={20} color="white" />
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e' }}>
                {editingSkill ? 'Edit Skill' : 'Create New Skill'}
              </h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                Define questions and output template for this skill
              </p>
            </div>
          </div>
          
          <form onSubmit={handleSaveSkill}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Skill Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={skillForm.name}
                  onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })}
                  placeholder="e.g., Audience, BI, Insights"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    background: 'white',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Description
                </label>
                <input
                  type="text"
                  value={skillForm.description}
                  onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })}
                  placeholder="Brief description shown to users"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    background: 'white'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Questions Checklist <span style={{ color: '#ef4444' }}>*</span>
                  <span style={{ fontWeight: '400', color: '#9ca3af', marginLeft: '8px' }}>Markdown</span>
                </label>
                <p style={{ margin: '0 0 8px', fontSize: '0.8rem', color: '#64748b' }}>
                  List the questions Carlos Mario should ask to gather information
                </p>
                <textarea
                  value={skillForm.questions}
                  onChange={(e) => setSkillForm({ ...skillForm, questions: e.target.value })}
                  placeholder="## Questions to Gather&#10;- What is the target audience?&#10;- What problem are we solving?&#10;- What is the timeline?"
                  rows={10}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                    background: 'white',
                    resize: 'vertical',
                    lineHeight: '1.5'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Output Template <span style={{ color: '#ef4444' }}>*</span>
                  <span style={{ fontWeight: '400', color: '#9ca3af', marginLeft: '8px' }}>Markdown</span>
                </label>
                <p style={{ margin: '0 0 8px', fontSize: '0.8rem', color: '#64748b' }}>
                  Template for the final document. Use {'{{placeholders}}'} for dynamic content
                </p>
                <textarea
                  value={skillForm.output_template}
                  onChange={(e) => setSkillForm({ ...skillForm, output_template: e.target.value })}
                  placeholder="# Brief Title&#10;&#10;## Overview&#10;{{overview}}&#10;&#10;## Target Audience&#10;{{audience}}&#10;&#10;## Requirements&#10;{{requirements}}"
                  rows={10}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                    background: 'white',
                    resize: 'vertical',
                    lineHeight: '1.5'
                  }}
                />
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              paddingTop: '16px', 
              borderTop: '1px solid #e2e8f0' 
            }}>
              <button type="submit" className="btn btn-primary" style={{ 
                padding: '12px 24px',
                borderRadius: '10px',
                fontWeight: '600'
              }}>
                <Save size={16} /> {editingSkill ? 'Update Skill' : 'Create Skill'}
              </button>
              <button 
                type="button" 
                onClick={() => { setShowSkillForm(false); setEditingSkill(null); }} 
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500',
                  color: '#64748b'
                }}
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {carlosSkills.length === 0 ? (
        <div className="admin-empty-state">
          <Sparkles size={48} />
          <h4>No Skills</h4>
          <p>Add skills to configure how Carlos Mario guides conversations.</p>
        </div>
      ) : (
        <div className="admin-list">
          {carlosSkills.map((skill) => (
            <div key={skill.id} className="admin-list-item" style={{ opacity: skill.is_active ? 1 : 0.6 }}>
              <div className="admin-list-item-info">
                <FileText size={20} className="admin-list-item-icon" />
                <div>
                  <span className="admin-list-item-name">{skill.name}</span>
                  {skill.description && <span className="admin-list-item-meta" style={{ display: 'block' }}>{skill.description}</span>}
                  <span className="admin-list-item-meta" style={{ display: 'block', fontSize: '0.75rem' }}>
                    {skill.is_active ? '✓ Active' : '○ Inactive'}
                  </span>
                </div>
              </div>
              <div className="admin-list-item-actions">
                <button
                  onClick={() => handleToggleSkillActive(skill)}
                  className="btn btn-icon"
                  title={skill.is_active ? 'Deactivate' : 'Activate'}
                  style={{ background: skill.is_active ? '#dcfce7' : '#f1f5f9', color: skill.is_active ? '#16a34a' : '#64748b' }}
                >
                  {skill.is_active ? '✓' : '○'}
                </button>
                <button onClick={() => handleEditSkill(skill)} className="btn btn-icon" title="Edit">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => handleDeleteSkill(skill.id)} className="btn btn-icon btn-danger" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPeopleSection = () => (
    <div className="card">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">People Management</h3>
          <p className="admin-section-description">Add and manage contacts/people in the system.</p>
        </div>
        <button onClick={() => setShowContactForm(!showContactForm)} className="btn btn-primary">
          <UserPlus size={16} /> Add Person
        </button>
      </div>

      {showContactForm && (
        <div className="admin-form-panel">
          <h4 className="admin-form-title">{editingContact ? 'Edit Person' : 'Add New Person'}</h4>
          <form onSubmit={handleSaveContact}>
            <div className="admin-form-grid admin-form-grid-3">
              <div className="admin-form-field">
                <label>Name *</label>
                <input type="text" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Contact name" required />
              </div>
              <div className="admin-form-field">
                <label>Phone Number *</label>
                <input type="tel" value={contactForm.number} onChange={(e) => setContactForm({ ...contactForm, number: e.target.value })} placeholder="e.g., 5511999999999" required />
              </div>
              <div className="admin-form-field">
                <label>Email</label>
                <input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="email@example.com" />
              </div>
            </div>
            <div className="admin-form-grid admin-form-grid-3">
              <div className="admin-form-field">
                <label>Role</label>
                <select value={contactForm.role_id} onChange={(e) => setContactForm({ ...contactForm, role_id: e.target.value })}>
                  <option value="">No role</option>
                  {roles.map((role) => (<option key={role.id} value={role.id}>{role.name}</option>))}
                </select>
              </div>
            </div>
            <div className="admin-form-grid admin-form-grid-3">
              <div className="admin-form-field">
                <label>Agency</label>
                <select value={contactForm.agency_id} onChange={(e) => setContactForm({ ...contactForm, agency_id: e.target.value })}>
                  <option value="">No agency</option>
                  {agencies.map((agency) => (<option key={agency.id} value={agency.id}>{agency.name}</option>))}
                </select>
              </div>
              <div className="admin-form-field">
                <label>Client</label>
                <select value={contactForm.people_client_id} onChange={(e) => setContactForm({ ...contactForm, people_client_id: e.target.value })}>
                  <option value="">No client</option>
                  {peopleClients.map((client) => (<option key={client.id} value={client.id}>{client.name}</option>))}
                </select>
              </div>
              <div className="admin-form-field">
                <label>Team</label>
                <select value={contactForm.people_team_id} onChange={(e) => setContactForm({ ...contactForm, people_team_id: e.target.value })}>
                  <option value="">No team</option>
                  {peopleTeams.map((team) => (<option key={team.id} value={team.id}>{team.name}</option>))}
                </select>
              </div>
            </div>
            <div className="admin-form-actions">
              <button type="submit" className="btn btn-primary">{editingContact ? 'Update' : 'Save'}</button>
              <button type="button" onClick={cancelContactForm} className="btn btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loadingContacts ? (
        <div className="loading">Loading people...</div>
      ) : contacts.length === 0 ? (
        <div className="admin-empty-state">
          <User size={48} />
          <h4>No People Yet</h4>
          <p>Add people using the button above.</p>
        </div>
      ) : (
        <>
          {/* Search & Filter Bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div className="search-wrapper" style={{ flex: '1 1 300px', maxWidth: '400px', margin: 0 }}>
              <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search by name, email, phone, role..."
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
                className="search-input-premium"
              />
            </div>
            <div style={{ color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>
              {filteredContacts.length === contacts.length ? (
                `Displaying ${contacts.length} person${contacts.length !== 1 ? 's' : ''} total`
              ) : (
                `Found ${filteredContacts.length} of ${contacts.length} person${contacts.length !== 1 ? 's' : ''}`
              )}
            </div>
          </div>

          {filteredContacts.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              color: '#64748b',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <AlertCircle size={40} style={{ color: '#94a3b8', marginBottom: '12px', opacity: 0.8 }} />
              <h4 style={{ color: '#1e293b', fontSize: '1.1rem', fontWeight: '700', margin: '0 0 6px 0' }}>
                No Matching Contacts
              </h4>
              <p style={{ fontSize: '0.9rem', margin: 0, color: '#64748b' }}>
                Try adjusting your search criteria or clear the search.
              </p>
            </div>
          ) : (
            <>
              <div className="people-table-container">
                <table className="people-table-premium">
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th style={{ width: '25%' }}>Identity</th>
                      <th style={{ width: '20%' }}>Phone</th>
                      <th style={{ width: '25%' }}>Email Address</th>
                      <th style={{ width: '15%' }}>Role</th>
                      <th style={{ width: '10%' }}>Source</th>
                      <th style={{ width: '5%', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedContacts.map((contact) => (
                      <tr key={contact.id} className="table-row-premium">
                        {/* IDENTITY */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div 
                              className="avatar-gradient" 
                              style={{ background: getAvatarGradient(contact.name) }}
                            >
                              {getInitials(contact.name)}
                            </div>
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>{contact.name}</span>
                          </div>
                        </td>

                        {/* PHONE */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                            <Phone size={14} style={{ color: '#94a3b8' }} />
                            {contact.number ? (
                              <a href={`tel:${contact.number}`} style={{ color: 'inherit', textDecoration: 'none', fontWeight: '500' }}>
                                {contact.number}
                              </a>
                            ) : (
                              <span style={{ color: '#cbd5e1' }}>N/A</span>
                            )}
                          </div>
                        </td>

                        {/* EMAIL */}
                        <td>
                          {contact.email ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                              <Mail size={14} style={{ color: '#94a3b8' }} />
                              <a href={`mailto:${contact.email}`} style={{ color: 'inherit', textDecoration: 'none', wordBreak: 'break-all', fontWeight: '500' }}>
                                {contact.email}
                              </a>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>

                        {/* ROLE */}
                        <td>
                          {contact.role_name ? (
                            <span className="admin-badge" style={{ background: contact.role_color ? `${contact.role_color}20` : '#f0f0f0', color: contact.role_color || '#333' }}>
                              <Tag size={12} /> {contact.role_name}
                            </span>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>

                        {/* SOURCE */}
                        <td>
                          <span className="admin-badge" style={{ 
                            background: contact.source === 'whatsapp' ? '#dcfce7' : contact.source === 'system' ? '#ede9fe' : '#f1f5f9', 
                            color: contact.source === 'whatsapp' ? '#15803d' : contact.source === 'system' ? '#6d28d9' : '#475569',
                            textTransform: 'uppercase',
                            fontSize: '0.72rem',
                            fontWeight: '700'
                          }}>
                            {contact.source || 'manual'}
                          </span>
                        </td>

                        {/* ACTIONS */}
                        <td>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEditContact(contact)}
                              className="action-btn-circle edit"
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteContact(contact.id, contact.name)}
                              className="action-btn-circle delete"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #f1f5f9'
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
                    Page {peoplePage} of {totalPages}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setPeoplePage(p => Math.max(1, p - 1))}
                      disabled={peoplePage === 1}
                      className="action-btn-circle"
                      style={{ cursor: peoplePage === 1 ? 'not-allowed' : 'pointer', opacity: peoplePage === 1 ? 0.5 : 1 }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setPeoplePage(p => Math.min(totalPages, p + 1))}
                      disabled={peoplePage === totalPages}
                      className="action-btn-circle"
                      style={{ cursor: peoplePage === totalPages ? 'not-allowed' : 'pointer', opacity: peoplePage === totalPages ? 0.5 : 1 }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );

  const renderRolesSection = () => (
    <div className="card">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">People Roles</h3>
          <p className="admin-section-description">Define roles that can be assigned to people/contacts.</p>
        </div>
      </div>

      <div className="admin-form-panel" style={{ border: 'none' }}>
        <form onSubmit={handleAddRole}>
          <div className="admin-form-grid" style={{ gridTemplateColumns: '1fr 1fr 48px' }}>
            <div className="admin-form-field">
              <label>Role Name *</label>
              <input type="text" placeholder="e.g., Developer, Manager" value={roleName} onChange={(e) => setRoleName(e.target.value)} required />
            </div>
            <div className="admin-form-field">
              <label>Description</label>
              <input type="text" placeholder="Optional description" value={roleDescription} onChange={(e) => setRoleDescription(e.target.value)} />
            </div>
            <div className="admin-form-field">
              <label>Color</label>
              <input type="color" value={roleColor} onChange={(e) => setRoleColor(e.target.value)} style={{ width: '100%', height: '44px', padding: '4px', cursor: 'pointer', border: '2px solid #e0e0e0', borderRadius: '8px' }} />
            </div>
          </div>
          <div className="admin-form-actions">
            <button className="btn btn-primary" type="submit"><Plus size={14} /> {editingRole ? 'Update Role' : 'Add Role'}</button>
            {editingRole && (<button className="btn btn-secondary" type="button" onClick={cancelEditRole}>Cancel</button>)}
          </div>
        </form>
      </div>

      {roles.length === 0 ? (
        <div className="admin-empty-state">
          <Tag size={48} />
          <h4>No Roles Yet</h4>
          <p>Add a role using the form above.</p>
        </div>
      ) : (
        <div className="admin-list">
          {roles.map((role) => (
            <div key={role.id} className="admin-list-item" style={{ borderLeft: `4px solid ${role.color || '#000'}` }}>
              <div className="admin-list-item-info">
                <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: role.color || '#000', flexShrink: 0 }} />
                <div>
                  <span className="admin-list-item-name">{role.name}</span>
                  {role.description && (<div className="admin-list-item-meta">{role.description}</div>)}
                </div>
              </div>
              <div className="admin-list-item-actions">
                <button onClick={() => handleEditRole(role)} className="admin-icon-btn" title="Edit role"><Edit2 size={16} /></button>
                <button onClick={() => handleDeleteRole(role.id)} className="admin-icon-btn danger" title="Delete role"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAccessSection = () => (
    <div className="card">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">Project Access Control</h3>
          <p className="admin-section-description">Assign users to projects. Users can only see projects and tasks they are assigned to.</p>
        </div>
      </div>

      <div className="admin-split-panel">
        <div className="admin-split-panel-left">
          <h4 className="admin-split-panel-title">Projects</h4>
          {projects.length === 0 ? (
            <p className="admin-list-item-meta">No projects yet.</p>
          ) : (
            <div className="admin-list">
              {projects.map((project) => (
                <button key={project.id} onClick={() => setSelectedProject(project)} className={`admin-project-btn ${selectedProject?.id === project.id ? 'active' : ''}`}>{project.name}</button>
              ))}
            </div>
          )}
        </div>

        <div>
          {selectedProject ? (
            <>
              <h4 className="admin-split-panel-title">Members of "{selectedProject.name}"</h4>
              <form onSubmit={handleAddMember} style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                <select className="select-control" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Select user to add...</option>
                  {availableUsers.map((user) => (<option key={user.id} value={user.id}>{user.name} ({user.email})</option>))}
                </select>
                <button className="btn btn-primary" type="submit" disabled={!selectedUserId}><UserPlus size={14} /> Add</button>
              </form>
              {projectMembers.length === 0 ? (
                <p className="admin-list-item-meta">No members assigned. Add users to give them access to this project.</p>
              ) : (
                <div className="admin-list">
                  {projectMembers.map((member) => (
                    <div key={member.id} className="admin-member-item">
                      <div>
                        <div className="admin-member-name">{member.name}</div>
                        <div className="admin-member-email">{member.email}</div>
                      </div>
                      <button onClick={() => handleRemoveMember(member.user_id)} className="admin-icon-btn danger" title="Remove member"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="admin-empty-state">
              <Shield size={48} />
              <h4>Select a Project</h4>
              <p>Choose a project from the list to manage its members.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPeopleAccessSection = () => (
    <div className="card">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">User Permissions & Access Control</h3>
          <p className="admin-section-description">Control user roles, access to the People module, Attendance module, and Planner editing privileges. Admins always have full access.</p>
        </div>
      </div>

      {users.length === 0 ? (
        <div className="admin-empty-state">
          <Users size={48} />
          <h4>No Users</h4>
          <p>No users found in the system.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Admin</th>
                <th style={{ textAlign: 'center' }}>Can View People</th>
                <th style={{ textAlign: 'center' }}>Can View All Attendance</th>
                <th style={{ textAlign: 'center' }}>Can Edit Planner</th>
              </tr>
            </thead>
            <tbody>
              {[...users].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="admin-list-item-info">
                      <User size={16} className="admin-list-item-icon" />
                      <span className="admin-list-item-name">{u.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="admin-list-item-meta">{u.email}</span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleAdminStatus(u.id, u.is_admin)}
                      disabled={u.id === user?.id}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '20px',
                        border: 'none',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: u.id === user?.id ? 'not-allowed' : 'pointer',
                        background: u.is_admin ? '#dbeafe' : '#f1f5f9',
                        color: u.is_admin ? '#2563eb' : '#64748b',
                        opacity: u.id === user?.id ? 0.7 : 1
                      }}
                      title={u.id === user?.id ? "You cannot demote yourself" : u.is_admin ? "Revoke Admin" : "Make Admin"}
                    >
                      {u.is_admin ? 'Admin' : 'Regular'}
                    </button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {u.is_admin ? (
                      <span style={{ color: '#16a34a', fontSize: '0.85rem' }}>✓ Always</span>
                    ) : (
                      <button
                        onClick={() => handleTogglePeopleAccess(u.id, u.can_view_people)}
                        style={{
                          padding: '6px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          background: u.can_view_people ? '#dcfce7' : '#f1f5f9',
                          color: u.can_view_people ? '#16a34a' : '#64748b'
                        }}
                      >
                        {u.can_view_people ? '✓ Enabled' : 'Disabled'}
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {u.is_admin ? (
                      <span style={{ color: '#16a34a', fontSize: '0.85rem' }}>✓ Always</span>
                    ) : (
                      <button
                        onClick={() => handleToggleAttendanceAccess(u.id, u.can_view_all_attendance)}
                        style={{
                          padding: '6px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          background: u.can_view_all_attendance ? '#dcfce7' : '#f1f5f9',
                          color: u.can_view_all_attendance ? '#16a34a' : '#64748b'
                        }}
                      >
                        {u.can_view_all_attendance ? '✓ Enabled' : 'Disabled'}
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {u.is_admin ? (
                      <span style={{ color: '#16a34a', fontSize: '0.85rem' }}>✓ Always</span>
                    ) : (
                      <button
                        onClick={() => handleTogglePlannerAccess(u.id, u.can_edit_planner)}
                        style={{
                          padding: '6px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          background: u.can_edit_planner ? '#dcfce7' : '#f1f5f9',
                          color: u.can_edit_planner ? '#16a34a' : '#64748b'
                        }}
                      >
                        {u.can_edit_planner ? '✓ Enabled' : 'Disabled'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );



  const renderAgentsSection = () => (
    <div className="card">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">AI Agent Configuration</h3>
          <p className="admin-section-description">Customize prompts and settings for each AI agent.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', marginTop: '20px' }}>
        {/* Agent list */}
        <div style={{ width: '280px', flexShrink: 0 }}>
          <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Select Agent</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {agentConfigs.map((agent) => (
              <button
                key={agent.agent_id}
                onClick={() => handleEditAgent(agent)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  border: editingAgent?.agent_id === agent.agent_id ? `2px solid ${agent.color}` : '2px solid #e2e8f0',
                  borderRadius: '10px',
                  background: editingAgent?.agent_id === agent.agent_id ? `${agent.color}10` : 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: agent.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bot size={18} color="white" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{agent.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{agent.role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Agent edit form */}
        <div style={{ flex: 1 }}>
          {editingAgent ? (
            <div style={{ background: '#fafbfc', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem' }}>
                  Editing: {editingAgent.name}
                </h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={cancelAgentForm} className="btn btn-secondary" style={{ padding: '8px 16px' }}>
                    Cancel
                  </button>
                  <button onClick={handleSaveAgent} disabled={savingAgent} className="btn btn-primary" style={{ padding: '8px 16px' }}>
                    <Save size={16} />
                    {savingAgent ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Agent Name
                  </label>
                  <input
                    type="text"
                    value={agentForm.name}
                    onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Role Title
                  </label>
                  <input
                    type="text"
                    value={agentForm.role}
                    onChange={(e) => setAgentForm({ ...agentForm, role: e.target.value })}
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Agent Color
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="color"
                      value={agentForm.color}
                      onChange={(e) => setAgentForm({ ...agentForm, color: e.target.value })}
                      style={{ width: '50px', height: '36px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{agentForm.color}</span>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Response Language
                  </label>
                  <select
                    value={agentForm.language}
                    onChange={(e) => setAgentForm({ ...agentForm, language: e.target.value })}
                    className="select-control"
                    style={{ width: '100%' }}
                  >
                    <option value="en">🇺🇸 English</option>
                    <option value="pt">🇧🇷 Português</option>
                    <option value="es">🇪🇸 Español</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Greeting Message
                  <span style={{ fontWeight: '400', color: '#94a3b8', marginLeft: '8px' }}>Shown when user starts a conversation</span>
                </label>
                <textarea
                  value={agentForm.greeting}
                  onChange={(e) => setAgentForm({ ...agentForm, greeting: e.target.value })}
                  className="form-input"
                  rows={2}
                  style={{ width: '100%', resize: 'vertical' }}
                  placeholder="Hi! I'm here to help you with..."
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  System Prompt
                  <span style={{ fontWeight: '400', color: '#94a3b8', marginLeft: '8px' }}>Instructions that define the agent's behavior</span>
                </label>
                <textarea
                  value={agentForm.system_prompt}
                  onChange={(e) => setAgentForm({ ...agentForm, system_prompt: e.target.value })}
                  className="form-input"
                  rows={10}
                  style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
                  placeholder="You are an AI assistant that..."
                />
                <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                  Tip: Be specific about the agent's role, capabilities, and response format.
                </p>
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Suggested Questions
                  <span style={{ fontWeight: '400', color: '#94a3b8', marginLeft: '8px' }}>Quick prompts shown to users (max 5)</span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {agentForm.suggested_prompts.map((prompt, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={prompt}
                        onChange={(e) => handlePromptChange(index, e.target.value)}
                        className="form-input"
                        style={{ flex: 1 }}
                        placeholder={`Question ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePrompt(index)}
                        style={{
                          padding: '8px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          background: 'white',
                          cursor: 'pointer',
                          color: '#ef4444'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {agentForm.suggested_prompts.length < 5 && (
                    <button
                      type="button"
                      onClick={handleAddPrompt}
                      style={{
                        padding: '8px 12px',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '6px',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#64748b',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Plus size={16} /> Add Question
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              background: '#fafbfc', 
              borderRadius: '12px', 
              padding: '40px', 
              border: '1px dashed #cbd5e1',
              textAlign: 'center',
              color: '#64748b'
            }}>
              <Bot size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <p style={{ margin: 0, fontWeight: '500' }}>Select an agent from the list to edit its configuration</p>
              <p style={{ margin: '8px 0 0', fontSize: '0.85rem' }}>
                You can customize the name, greeting, and system prompt for each AI agent.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Workspaces Section handlers
  const handleSaveWorkspace = async (e) => {
    e.preventDefault();
    if (!workspaceForm.name.trim() || !workspaceForm.agency_id) {
      alert('Name and Agency are required');
      return;
    }
    try {
      if (editingWorkspace) {
        await axios.put(`/api/workspaces/${editingWorkspace.id}`, {
          name: workspaceForm.name.trim(),
          description: workspaceForm.description.trim(),
          agency_id: parseInt(workspaceForm.agency_id),
          owner_contact_id: workspaceForm.owner_contact_id || null
        });
      } else {
        await axios.post('/api/workspaces', {
          name: workspaceForm.name.trim(),
          description: workspaceForm.description.trim(),
          agency_id: parseInt(workspaceForm.agency_id),
          owner_contact_id: workspaceForm.owner_contact_id || null
        });
      }
      setWorkspaceForm({ name: '', description: '', agency_id: '', owner_contact_id: '' });
      setEditingWorkspace(null);
      await loadWorkspacesLocal();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save workspace');
    }
  };

  const handleEditWorkspace = (workspace) => {
    setEditingWorkspace(workspace);
    setWorkspaceForm({
      name: workspace.name,
      description: workspace.description || '',
      agency_id: workspace.agency_id,
      owner_contact_id: workspace.owner_contact_id || ''
    });
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    if (!window.confirm('Are you sure you want to delete this workspace? All projects, tasks, sprints, sheets, journals, and docs in it will be permanently deleted.')) return;
    try {
      await axios.delete(`/api/workspaces/${workspaceId}`);
      if (selectedWorkspace?.id === workspaceId) {
        setSelectedWorkspace(null);
      }
      await loadWorkspacesLocal();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete workspace');
    }
  };

  const cancelEditWorkspace = () => {
    setEditingWorkspace(null);
    setWorkspaceForm({ name: '', description: '', agency_id: '', owner_contact_id: '' });
  };

  const handleAddWorkspaceMemberDirect = async (contactId) => {
    if (!contactId || !selectedWorkspace) return;
    try {
      await axios.post(`/api/workspaces/${selectedWorkspace.id}/members`, {
        contact_id: contactId
      });
      loadWorkspaceMembers(selectedWorkspace.id);
      loadContacts();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to add member to workspace');
    }
  };

  const handleRemoveWorkspaceMember = async (contactId) => {
    if (!window.confirm('Remove this person from the workspace?')) return;
    try {
      await axios.delete(`/api/workspaces/${selectedWorkspace.id}/members/${contactId}`);
      loadWorkspaceMembers(selectedWorkspace.id);
      loadContacts();
    } catch (error) {
      alert('Failed to remove member');
    }
  };

  const renderWorkspacesSection = () => {
    const nonMembers = contacts.filter(
      contact => !workspaceMembers.some(member => member.id === contact.id)
    );

    const canManageMembers = user?.is_admin || selectedWorkspace?.owner_contact_id === user?.contact_id;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div className="card">
          <h3 className="admin-section-title">
            <Briefcase size={20} />
            Workspaces
          </h3>
          <p className="admin-section-description">Create and manage workspaces scoped to specific agencies.</p>
          
          {user?.is_admin && (
            <form onSubmit={handleSaveWorkspace} style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: '700', color: '#1a1a2e' }}>
                {editingWorkspace ? '✏️ Edit Workspace' : '✨ Create New Workspace'}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Workspace Name *</label>
                  <input
                    type="text"
                    value={workspaceForm.name}
                    onChange={(e) => setWorkspaceForm({ ...workspaceForm, name: e.target.value })}
                    placeholder="e.g. Publicis Brazil Costs"
                    className="form-input"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Agency *</label>
                  <select
                    value={workspaceForm.agency_id}
                    onChange={(e) => setWorkspaceForm({ ...workspaceForm, agency_id: e.target.value })}
                    className="select-control"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    required
                  >
                    <option value="">Select agency...</option>
                    {agencies.map((agency) => (
                      <option key={agency.id} value={agency.id}>{agency.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Description</label>
                  <input
                    type="text"
                    value={workspaceForm.description}
                    onChange={(e) => setWorkspaceForm({ ...workspaceForm, description: e.target.value })}
                    placeholder="Optional description"
                    className="form-input"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Owner</label>
                  <select
                    value={workspaceForm.owner_contact_id}
                    onChange={(e) => setWorkspaceForm({ ...workspaceForm, owner_contact_id: e.target.value })}
                    className="select-control"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                  >
                    <option value="">Select owner...</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>{contact.name} ({contact.email || 'No email'})</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn btn-primary" style={{ padding: '9px 18px', borderRadius: '8px' }}>
                    <Plus size={16} />
                    {editingWorkspace ? 'Update' : 'Create'}
                  </button>
                  {editingWorkspace && (
                    <button type="button" onClick={cancelEditWorkspace} className="btn btn-secondary" style={{ padding: '9px 18px', borderRadius: '8px' }}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}

          {localWorkspaces.length === 0 ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No workspaces found.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Name</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Agency</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Owner</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Description</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600', color: '#475569', width: '280px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {localWorkspaces.map((ws) => (
                  <tr key={ws.id} style={{
                    borderBottom: '1px solid #f0f0f0',
                    background: selectedWorkspace?.id === ws.id ? 'rgba(102, 126, 234, 0.05)' : 'transparent'
                  }}>
                    <td style={{ padding: '12px 8px', fontWeight: '600', color: '#1e293b' }}>
                      {ws.name}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{ fontSize: '0.85rem', padding: '4px 10px', background: '#f1f5f9', borderRadius: '12px', color: '#475569', fontWeight: '500' }}>
                        {ws.agency_name || 'System'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px', color: '#475569', fontSize: '0.9rem' }}>
                      {ws.owner_name ? `${ws.owner_name}` : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>None</span>}
                    </td>
                    <td style={{ padding: '12px 8px', color: '#64748b', fontSize: '0.9rem' }}>{ws.description || '—'}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => setSelectedWorkspace(ws)}
                          className={`btn ${selectedWorkspace?.id === ws.id ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ padding: '6px 12px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <Users size={14} />
                          Members
                        </button>
                        {user?.is_admin && (
                          <>
                            <button onClick={() => handleEditWorkspace(ws)} className="btn btn-secondary" style={{ padding: '6px 10px' }} title="Edit Workspace">
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteWorkspace(ws.id)}
                              className="btn btn-danger"
                              style={{ padding: '6px 10px' }}
                              title="Delete Workspace"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Selected Workspace Members Panel */}
        {selectedWorkspace && (
          <div className="card" style={{
            borderLeft: '4px solid #667eea',
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 className="admin-section-title" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Users size={20} />
                  Workspace Members: {selectedWorkspace.name}
                  <span className="admin-badge" style={{ background: '#e0e7ff', color: '#4f46e5', fontSize: '0.8rem', padding: '2px 8px', borderRadius: '12px' }}>
                    {workspaceMembers.length} {workspaceMembers.length === 1 ? 'person' : 'people'}
                  </span>
                </h3>
                <p className="admin-section-description" style={{ margin: '4px 0 0' }}>
                  Assign contacts from the organization to this specific workspace.
                </p>
              </div>
              <button
                onClick={() => setSelectedWorkspace(null)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}
                title="Close Panel"
              >
                <X size={20} />
              </button>
            </div>

            {/* Add member search picker */}
            {canManageMembers && (
              <div style={{ 
                marginBottom: '20px', 
                padding: '16px', 
                background: '#f8fafc', 
                borderRadius: '12px', 
                border: '1.5px solid #e2e8f0',
                position: 'relative'
              }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Search & Add Members
                </label>
                
                <div style={{ position: 'relative', width: '100%' }}>
                  <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                  <input
                    type="text"
                    placeholder="Type name, email, or agency to search..."
                    value={memberSearchTerm}
                    onChange={(e) => {
                      setMemberSearchTerm(e.target.value);
                      setShowMemberDropdown(true);
                    }}
                    onFocus={() => setShowMemberDropdown(true)}
                    onBlur={() => {
                      // Small delay to allow clicking items in dropdown
                      setTimeout(() => setShowMemberDropdown(false), 200);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px 12px 42px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95rem',
                      color: '#1e293b',
                      background: 'white',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowMemberDropdown(false);
                      }
                    }}
                  />
                  {memberSearchTerm && (
                    <button
                      onClick={() => setMemberSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 0
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Dropdown list of filtered contacts */}
                {showMemberDropdown && memberSearchTerm && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    maxHeight: '240px',
                    overflowY: 'auto',
                    background: 'white',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '10px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 20,
                    padding: '6px'
                  }}>
                    {(() => {
                      const lower = memberSearchTerm.toLowerCase();
                      const filtered = nonMembers.filter(c => 
                        c.name?.toLowerCase().includes(lower) || 
                        c.email?.toLowerCase().includes(lower) ||
                        c.agency_name?.toLowerCase().includes(lower)
                      );

                      if (filtered.length === 0) {
                        return (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                            No matching contacts found (or they are already members).
                          </div>
                        );
                      }

                      return filtered.map((contact) => {
                        const initials = getInitials(contact.name);
                        const avatarColors = { bg: '#cbd5e1', text: '#475569' };
                        
                        return (
                          <div
                            key={contact.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              background: 'transparent',
                              transition: 'all 0.15s'
                            }}
                            onMouseDown={(e) => {
                              // Use onMouseDown instead of onClick to fire before onBlur hides the dropdown
                              e.preventDefault();
                              handleAddWorkspaceMemberDirect(contact.id);
                              setMemberSearchTerm('');
                              setShowMemberDropdown(false);
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                              {/* Avatar */}
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: avatarColors.bg,
                                color: avatarColors.text,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '700',
                                fontSize: '0.85rem',
                                flexShrink: 0
                              }}>
                                {initials}
                              </div>
                              
                              {/* Info */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{contact.name}</span>
                                  {contact.role_name && (
                                    <span style={{
                                      fontSize: '0.7rem',
                                      padding: '1px 6px',
                                      borderRadius: '4px',
                                      color: 'white',
                                      fontWeight: '600',
                                      background: contact.role_color || '#475569'
                                    }}>
                                      {contact.role_name}
                                    </span>
                                  )}
                                  <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: '4px', background: '#e2e8f0', color: '#475569', fontWeight: '500' }}>
                                    {contact.agency_name || 'System'}
                                  </span>
                                </div>
                                <span style={{ fontSize: '0.8rem', color: '#64748b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                  {contact.email || 'No email registered'}
                                </span>
                              </div>
                            </div>

                            {/* Quick Add Button */}
                            <button
                              type="button"
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                background: '#ecfdf5',
                                border: '1px solid #10b981',
                                color: '#047857',
                                fontWeight: '600',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#10b981';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#ecfdf5';
                                e.currentTarget.style.color = '#047857';
                              }}
                            >
                              <UserPlus size={13} />
                              Add
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Members table */}
            {workspaceMembers.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center', padding: '24px', background: '#f8fafc', borderRadius: '8px' }}>
                No members assigned to this workspace yet. Add someone using the form above!
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Name</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Role / Agency</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Email</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600', color: '#475569', width: '100px' }}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {workspaceMembers.map((member) => (
                    <tr key={member.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 8px', fontWeight: '600', color: '#1e293b' }}>{member.name}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {member.role_name && (
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              color: 'white',
                              fontWeight: '600',
                              background: member.role_color || '#475569'
                            }}>
                              {member.role_name}
                            </span>
                          )}
                          <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px', background: '#e2e8f0', color: '#475569', fontWeight: '500' }}>
                            {member.agency_name || 'System'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 8px', color: '#64748b', fontSize: '0.85rem' }}>{member.email || '—'}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {canManageMembers ? (
                          <button
                            onClick={() => handleRemoveWorkspaceMember(member.id)}
                            className="btn btn-danger"
                            style={{ padding: '6px 10px' }}
                            title="Remove from Workspace"
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>No Access</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  // Agencies Section
  const renderAgenciesSection = () => (
    <div className="card">
      <h3 className="admin-section-title">
        <Briefcase size={20} />
        Agencies
      </h3>
      <p className="admin-section-description">Manage agencies that people work for</p>
      
      <form onSubmit={handleSaveAgency} style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Agency Name *</label>
            <input
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder="Enter agency name"
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Description</label>
            <input
              type="text"
              value={agencyDescription}
              onChange={(e) => setAgencyDescription(e.target.value)}
              placeholder="Optional description"
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn btn-primary">
              <Plus size={16} />
              {editingAgency ? 'Update' : 'Add'}
            </button>
            {editingAgency && (
              <button type="button" onClick={cancelEditAgency} className="btn btn-secondary">Cancel</button>
            )}
          </div>
        </div>
      </form>

      {agencies.length === 0 ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No agencies yet. Add one above.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agencies.map((agency) => (
              <tr key={agency.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px', fontWeight: '500' }}>{agency.name}</td>
                <td style={{ padding: '12px 8px', color: '#64748b' }}>{agency.description || '—'}</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button onClick={() => handleEditAgency(agency)} className="btn btn-secondary" style={{ padding: '6px 10px' }}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDeleteAgency(agency.id)} className="btn btn-danger" style={{ padding: '6px 10px' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // People Clients Section
  const renderPeopleClientsSection = () => (
    <div className="card">
      <h3 className="admin-section-title">
        <Building2 size={20} />
        Clients
      </h3>
      <p className="admin-section-description">Manage clients that people work with</p>
      
      <form onSubmit={handleSavePeopleClient} style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Client Name *</label>
            <input
              type="text"
              value={peopleClientName}
              onChange={(e) => setPeopleClientName(e.target.value)}
              placeholder="Enter client name"
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Description</label>
            <input
              type="text"
              value={peopleClientDescription}
              onChange={(e) => setPeopleClientDescription(e.target.value)}
              placeholder="Optional description"
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn btn-primary">
              <Plus size={16} />
              {editingPeopleClient ? 'Update' : 'Add'}
            </button>
            {editingPeopleClient && (
              <button type="button" onClick={cancelEditPeopleClient} className="btn btn-secondary">Cancel</button>
            )}
          </div>
        </div>
      </form>

      {peopleClients.length === 0 ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No clients yet. Add one above.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {peopleClients.map((client) => (
              <tr key={client.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px', fontWeight: '500' }}>{client.name}</td>
                <td style={{ padding: '12px 8px', color: '#64748b' }}>{client.description || '—'}</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button onClick={() => handleEditPeopleClient(client)} className="btn btn-secondary" style={{ padding: '6px 10px' }}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDeletePeopleClient(client.id)} className="btn btn-danger" style={{ padding: '6px 10px' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // People Teams Section
  const renderPeopleTeamsSection = () => (
    <div className="card">
      <h3 className="admin-section-title">
        <UsersRound size={20} />
        Teams
      </h3>
      <p className="admin-section-description">Manage teams that people belong to</p>
      
      <form onSubmit={handleSavePeopleTeam} style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Team Name *</label>
            <input
              type="text"
              value={peopleTeamName}
              onChange={(e) => setPeopleTeamName(e.target.value)}
              placeholder="Enter team name"
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Description</label>
            <input
              type="text"
              value={peopleTeamDescription}
              onChange={(e) => setPeopleTeamDescription(e.target.value)}
              placeholder="Optional description"
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn btn-primary">
              <Plus size={16} />
              {editingPeopleTeam ? 'Update' : 'Add'}
            </button>
            {editingPeopleTeam && (
              <button type="button" onClick={cancelEditPeopleTeam} className="btn btn-secondary">Cancel</button>
            )}
          </div>
        </div>
      </form>

      {peopleTeams.length === 0 ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No teams yet. Add one above.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {peopleTeams.map((team) => (
              <tr key={team.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px', fontWeight: '500' }}>{team.name}</td>
                <td style={{ padding: '12px 8px', color: '#64748b' }}>{team.description || '—'}</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button onClick={() => handleEditPeopleTeam(team)} className="btn btn-secondary" style={{ padding: '6px 10px' }}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDeletePeopleTeam(team.id)} className="btn btn-danger" style={{ padding: '6px 10px' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div 
      className="admin-root-container"
      data-fullwidth="true"
      style={{
        display: 'flex',
        background: '#f8fafc',
        borderRadius: '0',
        border: 'none',
        height: 'calc(100vh - var(--header-height))',
        width: '100%',
        overflow: 'hidden',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif"
      }}
    >
      <style>{`
        .admin-root-container {
          --admin-primary: #4f46e5;
          --admin-primary-hover: #4338ca;
        }

        .admin-root-container,
        .admin-root-container * {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
          box-shadow: none !important;
        }

        /* Card Design System */
        .admin-root-container .card {
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 12px !important;
          padding: 24px !important;
          margin-bottom: 24px !important;
        }

        /* Headers & Typography */
        .admin-root-container h3.admin-section-title,
        .admin-root-container .admin-section-title {
          font-size: 1.15rem !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          letter-spacing: -0.02em !important;
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          margin-top: 0 !important;
          margin-bottom: 6px !important;
          border-bottom: none !important;
        }

        .admin-root-container p.admin-section-description,
        .admin-root-container .admin-section-description {
          font-size: 13px !important;
          color: #64748b !important;
          margin-top: 2px !important;
          margin-bottom: 20px !important;
          line-height: 1.4 !important;
        }

        /* Modern Labels & Inputs */
        .admin-root-container label {
          display: block !important;
          font-size: 12.5px !important;
          font-weight: 600 !important;
          color: #475569 !important;
          margin-bottom: 6px !important;
        }

        .admin-root-container input[type="text"],
        .admin-root-container input[type="email"],
        .admin-root-container input[type="password"],
        .admin-root-container select,
        .admin-root-container textarea {
          width: 100% !important;
          padding: 10px 14px !important;
          font-size: 14px !important;
          color: #0f172a !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          outline: none !important;
          transition: all 0.2s ease !important;
        }

        .admin-root-container input[type="text"]:focus,
        .admin-root-container input[type="email"]:focus,
        .admin-root-container select:focus,
        .admin-root-container textarea:focus {
          border-color: #4f46e5 !important;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important;
        }

        .admin-root-container input[type="color"] {
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          height: 40px !important;
          padding: 4px !important;
        }

        /* Buttons Redesign */
        .admin-root-container .btn,
        .admin-root-container button.admin-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          padding: 10px 18px !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          border: 1px solid transparent !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          outline: none !important;
        }

        .admin-root-container .btn-primary {
          background: #4f46e5 !important;
          color: #ffffff !important;
        }

        .admin-root-container .btn-primary:hover {
          background: #4338ca !important;
          transform: translateY(-1px) !important;
        }

        .admin-root-container .btn-secondary {
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          color: #475569 !important;
        }

        .admin-root-container .btn-secondary:hover {
          background: #f8fafc !important;
          border-color: #94a3b8 !important;
          color: #0f172a !important;
        }

        .admin-root-container .btn-danger {
          background: #ef4444 !important;
          color: #ffffff !important;
        }

        .admin-root-container .btn-danger:hover {
          background: #dc2626 !important;
          transform: translateY(-1px) !important;
        }

        .admin-root-container button:disabled,
        .admin-root-container .btn:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
          transform: none !important;
        }

        /* Tables Cohesive Theme */
        .admin-root-container table {
          width: 100% !important;
          border-collapse: collapse !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          margin-top: 16px !important;
          background: #ffffff !important;
        }

        .admin-root-container thead {
          background: #f8fafc !important;
        }

        .admin-root-container tr {
          border-bottom: 1px solid #f1f5f9 !important;
          transition: background 0.15s ease !important;
        }

        .admin-root-container tr:last-child {
          border-bottom: none !important;
        }

        .admin-root-container tr:hover {
          background: #f8fafc !important;
        }

        .admin-root-container th {
          padding: 12px 16px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #475569 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          border-bottom: 2px solid #e2e8f0 !important;
          text-align: left !important;
        }

        .admin-root-container td {
          padding: 14px 16px !important;
          font-size: 13.5px !important;
          color: #334155 !important;
          vertical-align: middle !important;
        }

        /* Modern Lists (Roles, Skills) */
        .admin-root-container .admin-list {
          display: flex !important;
          flex-direction: column !important;
          gap: 8px !important;
          margin-top: 16px !important;
        }

        .admin-root-container .admin-list-item {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          padding: 14px 18px !important;
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          margin-bottom: 0 !important;
          transition: all 0.2s ease !important;
        }

        .admin-root-container .admin-list-item:hover {
          border-color: #cbd5e1 !important;
          background: #f8fafc !important;
        }

        .admin-root-container .admin-list-item-info {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
        }

        .admin-root-container .admin-list-item-name {
          font-size: 14px !important;
          font-weight: 600 !important;
          color: #0f172a !important;
        }

        .admin-root-container .admin-list-item-meta {
          font-size: 12.5px !important;
          color: #64748b !important;
          margin-top: 2px !important;
        }

        .admin-root-container .admin-icon-btn {
          background: transparent !important;
          border: none !important;
          color: #64748b !important;
          padding: 6px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.15s !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .admin-root-container .admin-icon-btn:hover {
          background: #f1f5f9 !important;
          color: #0f172a !important;
        }

        .admin-root-container .admin-icon-btn.danger:hover {
          background: #fef2f2 !important;
          color: #ef4444 !important;
        }

        /* Empty State */
        .admin-root-container .admin-empty-state {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 40px 20px !important;
          text-align: center !important;
          color: #64748b !important;
          background: #f8fafc !important;
          border: 1px dashed #cbd5e1 !important;
          border-radius: 8px !important;
          margin-top: 16px !important;
        }

        .admin-root-container .admin-empty-state svg {
          color: #94a3b8 !important;
          margin-bottom: 12px !important;
        }

        .admin-root-container .admin-empty-state h4 {
          font-size: 14px !important;
          font-weight: 600 !important;
          color: #334155 !important;
          margin: 0 0 4px 0 !important;
        }

        .admin-root-container .admin-empty-state p {
          font-size: 12.5px !important;
          margin: 0 !important;
          color: #64748b !important;
        }

        /* Autocomplete Picker Styles */
        .admin-root-container .member-search-container {
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          background: #ffffff !important;
        }

        .admin-root-container .search-result-item {
          transition: background 0.15s ease !important;
        }

        .admin-root-container .search-result-item:hover {
          background: #f8fafc !important;
        }
      `}</style>

      {/* RIGHT SIDE CONTENT PANE */}
      <div 
        className="admin-content-pane"
        style={{
          flex: 1,
          padding: '32px 40px',
          overflowY: 'auto',
          background: '#f8fafc',
          height: '100%'
        }}
      >
        {user?.is_admin && (
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'rgba(79, 70, 229, 0.08)',
                color: '#4f46e5',
                padding: '10px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Mail size={18} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>Email Daily Digest</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Notify team of daily changes</span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {digestResult && (
                <div style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: digestResult.success ? '#16a34a' : '#dc2626',
                  background: digestResult.success ? '#f0fdf4' : '#fef2f2',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: digestResult.success ? '1px solid #bbf7d0' : '1px solid #fecaca',
                }}>
                  {digestResult.success
                    ? `Sent ${digestResult.sent} email${digestResult.sent !== 1 ? 's' : ''}${digestResult.failed ? `, ${digestResult.failed} failed` : ''}`
                    : digestResult.error || 'Failed to send'}
                </div>
              )}

              <button
                onClick={handleSendDigest}
                disabled={sendingDigest}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: sendingDigest ? '#cbd5e1' : '#4f46e5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: sendingDigest ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  outline: 'none',
                  boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)'
                }}
                onMouseEnter={(e) => {
                  if (!sendingDigest) e.currentTarget.style.background = '#4338ca';
                }}
                onMouseLeave={(e) => {
                  if (!sendingDigest) e.currentTarget.style.background = '#4f46e5';
                }}
              >
                {sendingDigest ? 'Sending...' : 'Send Daily Digest'}
              </button>
            </div>
          </div>
        )}
        {activeSection === 'workspaces' && renderWorkspacesSection()}
        {activeSection === 'skills' && renderSkillsSection()}
        {activeSection === 'people' && renderPeopleSection()}
        {activeSection === 'roles' && renderRolesSection()}
        {activeSection === 'agencies' && renderAgenciesSection()}
        {activeSection === 'people_clients' && renderPeopleClientsSection()}
        {activeSection === 'people_teams' && renderPeopleTeamsSection()}
        {activeSection === 'access' && renderAccessSection()}
        {activeSection === 'people_access' && renderPeopleAccessSection()}

        {activeSection === 'agents' && renderAgentsSection()}
        {activeSection === 'office_bi' && (
          <OfficeBiSection
            users={users}
            officeBiData={officeBiData}
            loadingOfficeBi={loadingOfficeBi}
            officeBiError={officeBiError}
            loadOfficeBiData={loadOfficeBiData}
            biDateRange={biDateRange}
            setBiDateRange={setBiDateRange}
            biCustomStart={biCustomStart}
            setBiCustomStart={setBiCustomStart}
            biCustomEnd={biCustomEnd}
            setBiCustomEnd={setBiCustomEnd}
            biSelectedUser={biSelectedUser}
            setBiSelectedUser={setBiSelectedUser}
            biSelectedTeam={biSelectedTeam}
            setBiSelectedTeam={setBiSelectedTeam}
            biSelectedClient={biSelectedClient}
            setBiSelectedClient={setBiSelectedClient}
            biSelectedAgency={biSelectedAgency}
            setBiSelectedAgency={setBiSelectedAgency}
            biSubTab={biSubTab}
            setBiSubTab={setBiSubTab}
          />
        )}
      </div>
    </div>
  );
}

// ─── OFFICE BI REPORT & ANALYTICS MODULE ─────────────────────────────────────

function todayIsoLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function biIsoWeekday(d) {
  return (d.getDay() + 6) % 7;
}

function buildBiGridCells(year, half) {
  const startMonth = half === 1 ? 0 : 6;
  const endMonth = half === 1 ? 5 : 11;
  const halfStart = new Date(year, startMonth, 1);
  const halfEnd = new Date(year, endMonth + 1, 0);

  const firstColStart = new Date(halfStart);
  firstColStart.setDate(firstColStart.getDate() - biIsoWeekday(halfStart));

  const lastColEnd = new Date(halfEnd);
  lastColEnd.setDate(lastColEnd.getDate() + (6 - biIsoWeekday(halfEnd)));
  const dayMs = 24 * 60 * 60 * 1000;
  const totalDays = Math.round((lastColEnd - firstColStart) / dayMs) + 1;
  const numWeeks = Math.ceil(totalDays / 7);

  const cells = [];
  for (let i = 0; i < numWeeks * 7; i++) {
    const d = new Date(firstColStart);
    d.setDate(d.getDate() + i);
    const col = Math.floor(i / 7);
    const row = i % 7;
    const inHalf = d >= halfStart && d <= halfEnd && d.getFullYear() === year;
    const iso = inHalf
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : null;
    cells.push({
      date: iso,
      col,
      row,
      isWeekend: row >= 5,
      isOutOfYear: !inHalf,
      _jsDate: d
    });
  }
  return cells;
}

function buildBiMonthLabelPositions(cells, year, half) {
  const startMonth = half === 1 ? 0 : 6;
  const endMonth = half === 1 ? 5 : 11;
  const positions = [];
  for (let m = startMonth; m <= endMonth; m++) {
    const firstCell = cells.find((c) => !c.isOutOfYear && c._jsDate.getFullYear() === year && c._jsDate.getMonth() === m && c._jsDate.getDate() === 1);
    if (firstCell) positions.push({ month: m, col: firstCell.col });
  }
  return positions;
}

const BI_COLORS = {
  EMPTY: '#f1f5f9',
  PLANNED: '#fca5a5',
  FUTURE_PLANNED: '#c7d2fe',
  ATTENDED: '#22c55e',
  DROPIN: '#86efac',
  TODAY: '#0f172a'
};

function OfficeBiSection({
  users,
  officeBiData,
  loadingOfficeBi,
  officeBiError,
  loadOfficeBiData,
  biDateRange,
  setBiDateRange,
  biCustomStart,
  setBiCustomStart,
  biCustomEnd,
  setBiCustomEnd,
  biSelectedUser,
  setBiSelectedUser,
  biSelectedTeam,
  setBiSelectedTeam,
  biSelectedClient,
  setBiSelectedClient,
  biSelectedAgency,
  setBiSelectedAgency,
  biSubTab,
  setBiSubTab
}) {
  const today = todayIsoLocal();
  const [biYear, setBiYear] = useState(new Date().getFullYear());
  const [hoveredBar, setHoveredBar] = useState(null);

  // Set default custom date bounds if empty
  useEffect(() => {
    if (!biCustomStart || !biCustomEnd) {
      const todayVal = todayIsoLocal();
      const d = new Date();
      d.setDate(d.getDate() - 29);
      const thirtyDaysAgoVal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setBiCustomStart(thirtyDaysAgoVal);
      setBiCustomEnd(todayVal);
    }
  }, [biCustomStart, biCustomEnd, setBiCustomStart, setBiCustomEnd]);

  // Extract unique filters dynamically
  const biTeams = useMemo(() => {
    const set = new Set();
    officeBiData.forEach(r => r.team_name && set.add(r.team_name));
    return Array.from(set).sort();
  }, [officeBiData]);

  const biClients = useMemo(() => {
    const set = new Set();
    officeBiData.forEach(r => r.client_name && set.add(r.client_name));
    return Array.from(set).sort();
  }, [officeBiData]);

  const biAgencies = useMemo(() => {
    const set = new Set();
    officeBiData.forEach(r => r.agency_name && set.add(r.agency_name));
    return Array.from(set).sort();
  }, [officeBiData]);

  const getBiDateRangeBounds = useCallback(() => {
    const todayD = new Date();
    const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    
    let startStr = '';
    let endStr = toIso(todayD);
    
    if (biDateRange === 'this_month') {
      const start = new Date(todayD.getFullYear(), todayD.getMonth(), 1);
      startStr = toIso(start);
    } else if (biDateRange === 'last_30_days') {
      const start = new Date();
      start.setDate(todayD.getDate() - 29);
      startStr = toIso(start);
    } else if (biDateRange === 'this_quarter') {
      const quarter = Math.floor(todayD.getMonth() / 3);
      const start = new Date(todayD.getFullYear(), quarter * 3, 1);
      startStr = toIso(start);
    } else if (biDateRange === 'this_year') {
      startStr = `${todayD.getFullYear()}-01-01`;
    } else if (biDateRange === 'custom') {
      startStr = biCustomStart;
      endStr = biCustomEnd;
    }
    return { startStr, endStr };
  }, [biDateRange, biCustomStart, biCustomEnd]);

  const filteredBiData = useMemo(() => {
    if (!officeBiData || officeBiData.length === 0) return [];
    const { startStr, endStr } = getBiDateRangeBounds();
    
    return officeBiData.filter((row) => {
      if (startStr && row.date < startStr) return false;
      if (endStr && row.date > endStr) return false;
      if (biSelectedUser !== 'all' && String(row.user_id) !== String(biSelectedUser)) return false;
      if (biSelectedTeam !== 'all' && row.team_name !== biSelectedTeam) return false;
      if (biSelectedClient !== 'all' && row.client_name !== biSelectedClient) return false;
      if (biSelectedAgency !== 'all' && row.agency_name !== biSelectedAgency) return false;
      return true;
    });
  }, [officeBiData, getBiDateRangeBounds, biSelectedUser, biSelectedTeam, biSelectedClient, biSelectedAgency]);

  const biMetrics = useMemo(() => {
    const data = filteredBiData;
    let totalPlanned = 0;
    let plannedAndAttended = 0;
    let totalAttended = 0;
    let noShows = 0;
    let dropIns = 0;
    
    const userRecords = {};
    data.forEach(r => {
      if (!userRecords[r.user_id]) userRecords[r.user_id] = [];
      userRecords[r.user_id].push(r);
    });
    
    const userIds = Object.keys(userRecords);
    
    data.forEach(row => {
      const isPast = row.date < today;
      const isWorkday = new Date(row.date + 'T00:00:00').getDay() % 6 !== 0;
      
      if (row.planned) {
        totalPlanned++;
        if (row.checked_in_at) {
          plannedAndAttended++;
        } else if (isPast && isWorkday) {
          noShows++;
        }
      } else {
        if (row.checked_in_at) {
          dropIns++;
        }
      }
      
      if (row.checked_in_at) {
        totalAttended++;
      }
    });
    
    const { startStr, endStr } = getBiDateRangeBounds();
    let numWeeks = 4.3;
    if (startStr && endStr) {
      const msDiff = new Date(endStr) - new Date(startStr);
      const daysDiff = Math.max(1, Math.round(msDiff / (1000 * 60 * 60 * 24)) + 1);
      numWeeks = Math.max(1, daysDiff / 7);
    }
    
    const complianceRate = totalPlanned > 0 ? Math.round((plannedAndAttended / totalPlanned) * 100) : 100;
    const noShowRate = totalPlanned > 0 ? Math.round((noShows / totalPlanned) * 100) : 0;
    const dropInRate = totalAttended > 0 ? Math.round((dropIns / totalAttended) * 100) : 0;
    const avgCheckinsPerWeek = userIds.length > 0
      ? (totalAttended / userIds.length / numWeeks).toFixed(1)
      : '0.0';
      
    return {
      totalPlanned,
      plannedAndAttended,
      totalAttended,
      noShows,
      dropIns,
      complianceRate,
      noShowRate,
      dropInRate,
      avgCheckinsPerWeek,
      userCount: userIds.length
    };
  }, [filteredBiData, getBiDateRangeBounds, today]);

  // Aggregate daily check-ins for the SVG chart
  const biDailyTrend = useMemo(() => {
    const { startStr, endStr } = getBiDateRangeBounds();
    if (!startStr || !endStr) return [];
    
    const dates = [];
    let curr = new Date(startStr + 'T00:00:00');
    const last = new Date(endStr + 'T00:00:00');
    while (curr <= last) {
      const y = curr.getFullYear();
      const m = String(curr.getMonth() + 1).padStart(2, '0');
      const d = String(curr.getDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
      curr.setDate(curr.getDate() + 1);
    }
    
    const counts = {};
    filteredBiData.forEach(row => {
      if (row.checked_in_at) {
        counts[row.date] = (counts[row.date] || 0) + 1;
      }
    });
    
    return dates.map(dateStr => ({
      date: dateStr,
      count: counts[dateStr] || 0,
      label: new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    }));
  }, [filteredBiData, getBiDateRangeBounds]);

  // Check-ins by day of the week
  const biDayOfWeekTrend = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    let totalWorkdaysCheckins = 0;
    
    filteredBiData.forEach(row => {
      if (row.checked_in_at) {
        const d = new Date(row.date + 'T00:00:00');
        const day = d.getDay();
        if (day >= 1 && day <= 5) {
          counts[day - 1]++;
          totalWorkdaysCheckins++;
        }
      }
    });
    
    const labels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    return counts.map((count, index) => ({
      label: labels[index],
      count,
      pct: totalWorkdaysCheckins > 0 ? Math.round((count / totalWorkdaysCheckins) * 100) : 0
    }));
  }, [filteredBiData]);

  // Today's Status (Who's in, who's expected)
  const biTodayStatus = useMemo(() => {
    const checkedIn = [];
    const expected = [];
    
    officeBiData.forEach(row => {
      if (row.date === today) {
        if (row.checked_in_at) {
          checkedIn.push(row);
        } else if (row.planned) {
          expected.push(row);
        }
      }
    });
    
    return { checkedIn, expected };
  }, [officeBiData, today]);

  // Individual person analytics
  const selectedUserMetrics = useMemo(() => {
    if (biSelectedUser === 'all') return null;
    
    const userRows = officeBiData.filter(r => String(r.user_id) === String(biSelectedUser));
    const name = users.find(u => String(u.id) === String(biSelectedUser))?.name || 'User';
    const email = users.find(u => String(u.id) === String(biSelectedUser))?.email || '';
    
    let totalCheckins = 0;
    let totalPlanned = 0;
    let plannedAndAttended = 0;
    let noShows = 0;
    let dropIns = 0;
    
    userRows.forEach(row => {
      const isPastOrToday = row.date <= today;
      const isWorkday = new Date(row.date + 'T00:00:00').getDay() % 6 !== 0;
      
      if (row.checked_in_at) {
        totalCheckins++;
      }
      if (row.planned) {
        totalPlanned++;
        if (row.checked_in_at) {
          plannedAndAttended++;
        } else if (isPastOrToday && isWorkday) {
          noShows++;
        }
      } else {
        if (row.checked_in_at) {
          dropIns++;
        }
      }
    });

    const userWeeks = {};
    userRows.forEach(row => {
      if (row.checked_in_at) {
        const rd = new Date(row.date + 'T00:00:00');
        const monday = new Date(rd);
        monday.setDate(monday.getDate() - biIsoWeekday(monday));
        const mondayIso = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
        userWeeks[mondayIso] = (userWeeks[mondayIso] || 0) + 1;
      }
    });
    
    const weeks = Object.keys(userWeeks);
    const weeksMetTarget = weeks.filter(w => userWeeks[w] >= 3).length;
    const targetMetPct = weeks.length > 0 ? Math.round((weeksMetTarget / weeks.length) * 100) : 0;
    const complianceRate = totalPlanned > 0 ? Math.round((plannedAndAttended / totalPlanned) * 100) : 100;

    return {
      name,
      email,
      totalCheckins,
      totalPlanned,
      plannedAndAttended,
      noShows,
      dropIns,
      complianceRate,
      weeksActive: weeks.length,
      weeksMetTarget,
      targetMetPct,
      recentLogs: [...userRows].slice(0, 10)
    };
  }, [officeBiData, biSelectedUser, users, today]);

  // Mini contributions calendar variables
  const cellsH1 = useMemo(() => buildBiGridCells(biYear, 1), [biYear]);
  const cellsH2 = useMemo(() => buildBiGridCells(biYear, 2), [biYear]);
  const monthPositionsH1 = useMemo(() => buildBiMonthLabelPositions(cellsH1, biYear, 1), [cellsH1, biYear]);
  const monthPositionsH2 = useMemo(() => buildBiMonthLabelPositions(cellsH2, biYear, 2), [cellsH2, biYear]);

  const selectedUserRowsMap = useMemo(() => {
    const map = new Map();
    if (biSelectedUser !== 'all') {
      officeBiData
        .filter(r => String(r.user_id) === String(biSelectedUser))
        .forEach(r => map.set(r.date, r));
    }
    return map;
  }, [officeBiData, biSelectedUser]);

  // Format time utility
  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // SVG Chart Dimensions & Computations
  const svgWidth = 600;
  const svgHeight = 240;
  const paddingLeft = 40;
  const paddingRight = 15;
  const paddingTop = 20;
  const paddingBottom = 35;
  const chartWidth = svgWidth - paddingLeft - paddingRight;
  const chartHeight = svgHeight - paddingTop - paddingBottom;
  
  const maxDailyCount = biDailyTrend.reduce((max, item) => Math.max(max, item.count), 0);
  const yMax = Math.max(5, Math.ceil(maxDailyCount * 1.2));
  
  const barWidth = biDailyTrend.length > 0 ? (chartWidth / biDailyTrend.length) * 0.7 : 10;
  const barGap = biDailyTrend.length > 0 ? (chartWidth / biDailyTrend.length) * 0.3 : 4;

  if (loadingOfficeBi) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '16px' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: 500 }}>Generating Business Intelligence insights...</p>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (officeBiError) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center', borderColor: '#fecaca', background: '#fef2f2', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <AlertCircle size={36} style={{ color: '#dc2626' }} />
        <h4 style={{ color: '#b91c1c', margin: '0' }}>Failed to load BI reports</h4>
        <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 8px 0' }}>{officeBiError}</p>
        <button onClick={loadOfficeBiData} className="btn btn-primary">Retry loading</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
      {/* Page Title */}
      <div>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BarChart3 size={24} style={{ color: '#667eea' }} />
          Office Analytics & BI
        </h2>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.88rem' }}>
          Observe organization-wide daily check-ins, plan compliance, drop-in rates, and individual performance.
        </p>
      </div>

      {/* FILTER PANEL CARD */}
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        padding: '20px 24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          {/* Date range filter */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Date Range</label>
            <select
              value={biDateRange}
              onChange={(e) => setBiDateRange(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
            >
              <option value="this_month">This Month</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="this_quarter">This Quarter</option>
              <option value="this_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* User selector */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>People Filter</label>
            <select
              value={biSelectedUser}
              onChange={(e) => {
                setBiSelectedUser(e.target.value);
                if (e.target.value !== 'all') {
                  setBiSubTab('people');
                }
              }}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
            >
              <option value="all">All People</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Team filter */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Team</label>
            <select
              value={biSelectedTeam}
              onChange={(e) => setBiSelectedTeam(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
            >
              <option value="all">All Teams</option>
              {biTeams.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Client filter */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Client</label>
            <select
              value={biSelectedClient}
              onChange={(e) => setBiSelectedClient(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
            >
              <option value="all">All Clients</option>
              {biClients.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Agency filter */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Agency</label>
            <select
              value={biSelectedAgency}
              onChange={(e) => setBiSelectedAgency(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
            >
              <option value="all">All Agencies</option>
              {biAgencies.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Custom Date Bounds */}
        {biDateRange === 'custom' && (
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', marginTop: '4px', alignSelf: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>Start Date:</span>
              <input
                type="date"
                value={biCustomStart}
                onChange={(e) => setBiCustomStart(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>End Date:</span>
              <input
                type="date"
                value={biCustomEnd}
                onChange={(e) => setBiCustomEnd(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* DASHBOARD TAB SUB-NAVIGATION */}
      <div style={{ display: 'inline-flex', padding: '4px', background: '#f1f5f9', borderRadius: '10px', gap: '4px', alignSelf: 'flex-start' }}>
        <button
          type="button"
          onClick={() => setBiSubTab('overview')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '0.88rem',
            fontWeight: biSubTab === 'overview' ? 600 : 500,
            color: biSubTab === 'overview' ? '#4f46e5' : '#64748b',
            background: biSubTab === 'overview' ? '#ffffff' : 'transparent',
            boxShadow: biSubTab === 'overview' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Overview Dashboard
        </button>
        <button
          type="button"
          onClick={() => {
            setBiSubTab('people');
            if (biSelectedUser === 'all' && users.length > 0) {
              setBiSelectedUser(String(users[0].id));
            }
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '0.88rem',
            fontWeight: biSubTab === 'people' ? 600 : 500,
            color: biSubTab === 'people' ? '#4f46e5' : '#64748b',
            background: biSubTab === 'people' ? '#ffffff' : 'transparent',
            boxShadow: biSubTab === 'people' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Per-Person Analytics
        </button>
      </div>

      {/* VIEW CONDITIONAL RENDERING */}
      {biSubTab === 'overview' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* METRIC CARDS ROW */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            <div style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '1px solid #c7d2fe', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Attendance Rate</span>
              <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1e1b4b' }}>{biMetrics.avgCheckinsPerWeek} d/wk</h3>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#4338ca' }}>Avg check-ins per person/week</p>
            </div>
            
            <div style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '1px solid #a7f3d0', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan Compliance</span>
              <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#064e3b' }}>{biMetrics.complianceRate}%</h3>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#047857' }}>Checked in when planned</p>
            </div>

            <div style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', border: '1px solid #fecaca', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>No-Show Rate</span>
              <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#7f1d1d' }}>{biMetrics.noShowRate}%</h3>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#b91c1c' }}>Missed office days planned</p>
            </div>

            <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid #fde68a', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Drop-in Rate</span>
              <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#78350f' }}>{biMetrics.dropInRate}%</h3>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#b45309' }}>Check-ins without pre-planning</p>
            </div>
          </div>

          {/* TREND CHART & DOW GRID */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', alignItems: 'stretch', flexWrap: 'wrap' }}>
            {/* SVG Trend Chart */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', position: 'relative' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Daily Check-ins Trend</h4>
              
              {biDailyTrend.length === 0 ? (
                <div style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>No data in range</div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <svg width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" />
                        <stop offset="100%" stopColor="#86efac" />
                      </linearGradient>
                    </defs>
                    
                    {/* Background Grid Lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((val, idx) => {
                      const y = paddingTop + chartHeight * (1 - val);
                      const gridVal = Math.round(yMax * val);
                      return (
                        <g key={idx}>
                          <line x1={paddingLeft} y1={y} x2={svgWidth - paddingRight} y2={y} stroke="#f1f5f9" strokeDasharray="3" />
                          <text x={paddingLeft - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8" fontWeight="500">{gridVal}</text>
                        </g>
                      );
                    })}

                    {/* Bars */}
                    {biDailyTrend.map((item, idx) => {
                      const barHeight = (item.count / yMax) * chartHeight;
                      const x = paddingLeft + idx * (barWidth + barGap);
                      const y = paddingTop + chartHeight - barHeight;
                      const isHovered = hoveredBar && hoveredBar.index === idx;
                      
                      return (
                        <g key={item.date}>
                          <rect
                            x={x}
                            y={y}
                            width={barWidth}
                            height={Math.max(1, barHeight)}
                            fill={isHovered ? '#16a34a' : 'url(#barGrad)'}
                            rx="3"
                            style={{ transition: 'all 0.15s ease', cursor: 'pointer' }}
                          />
                          {/* Invisible hover overlay to make hovering easier */}
                          <rect
                            x={x - barGap/2}
                            y={paddingTop}
                            width={barWidth + barGap}
                            height={chartHeight}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => {
                              setHoveredBar({
                                index: idx,
                                date: item.date,
                                count: item.count,
                                x: x + barWidth/2,
                                y: y - 10
                              });
                            }}
                            onMouseLeave={() => setHoveredBar(null)}
                          />
                        </g>
                      );
                    })}

                    {/* X Axis line */}
                    <line x1={paddingLeft} y1={paddingTop + chartHeight} x2={svgWidth - paddingRight} y2={paddingTop + chartHeight} stroke="#e2e8f0" strokeWidth="1" />
                    
                    {/* X axis labels (thinned) */}
                    {biDailyTrend.map((item, idx) => {
                      const showLabel = biDailyTrend.length <= 15 || idx % Math.ceil(biDailyTrend.length / 8) === 0;
                      if (!showLabel) return null;
                      const x = paddingLeft + idx * (barWidth + barGap) + barWidth / 2;
                      return (
                        <text
                          key={item.date}
                          x={x}
                          y={paddingTop + chartHeight + 16}
                          textAnchor="middle"
                          fontSize="9.5"
                          fill="#94a3b8"
                          fontWeight="500"
                        >
                          {item.label}
                        </text>
                      );
                    })}
                  </svg>

                  {/* HTML Hover Tooltip */}
                  {hoveredBar && (
                    <div style={{
                      position: 'absolute',
                      left: `${(hoveredBar.x / svgWidth) * 100}%`,
                      top: `${(hoveredBar.y / svgHeight) * 100}%`,
                      transform: 'translate(-50%, -100%)',
                      background: '#0f172a',
                      color: '#ffffff',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}>
                      <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: '500', marginBottom: '2px' }}>{hoveredBar.date}</div>
                      <div>{hoveredBar.count} check-in{hoveredBar.count !== 1 ? 's' : ''}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Horizontal Day-of-week Breakdown */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ margin: '0 0 18px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Weekly Distribution</h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, justifyContent: 'center' }}>
                {biDayOfWeekTrend.map((item) => (
                  <div key={item.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 500, color: '#475569' }}>{item.label}</span>
                      <span style={{ fontWeight: 600, color: '#1e293b' }}>{item.count} ({item.pct}%)</span>
                    </div>
                    <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${item.pct}%`, background: 'linear-gradient(90deg, #22c55e 0%, #86efac 100%)', borderRadius: '99px', transition: 'width 0.3s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* WHO'S IN TODAY & STATS DETAILS */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start', flexWrap: 'wrap' }}>
            {/* Checked In Today */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Checked In Today</h4>
                <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.78rem', fontWeight: 600, padding: '3px 8px', borderRadius: '12px' }}>{biTodayStatus.checkedIn.length} present</span>
              </div>

              {biTodayStatus.checkedIn.length === 0 ? (
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '16px 0' }}>No one has checked in today yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
                  {biTodayStatus.checkedIn.map(row => (
                    <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{row.user_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.team_name || 'No team'} · {row.agency_name || 'No agency'}</div>
                      </div>
                      <span style={{ fontSize: '0.78rem', color: '#15803d', fontWeight: 600, background: '#e8f5e9', padding: '4px 8px', borderRadius: '6px' }}>In {formatTime(row.checked_in_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Expected Today */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Planned But Not Checked In</h4>
                <span style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.78rem', fontWeight: 600, padding: '3px 8px', borderRadius: '12px' }}>{biTodayStatus.expected.length} expected</span>
              </div>

              {biTodayStatus.expected.length === 0 ? (
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '16px 0' }}>All expected users have checked in, or no check-ins were planned.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
                  {biTodayStatus.expected.map(row => (
                    <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{row.user_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.team_name || 'No team'} · {row.agency_name || 'No agency'}</div>
                      </div>
                      <span style={{ fontSize: '0.78rem', color: '#d97706', fontWeight: 600, background: '#fffbeb', padding: '4px 8px', borderRadius: '6px' }}>Expected today</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* PER-PERSON ANALYTICS VIEW */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* User selector */}
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Select a Person to View Analytics</h4>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <select
                value={biSelectedUser}
                onChange={(e) => setBiSelectedUser(e.target.value)}
                style={{ minWidth: '240px', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', background: '#ffffff', color: '#1e293b', cursor: 'pointer' }}
              >
                <option value="all" disabled>Select a person...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* User metrics & Heatmap details */}
          {selectedUserMetrics ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* User Header Profile Card */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#ffffff',
                borderRadius: '16px',
                padding: '24px 32px',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{selectedUserMetrics.name}</h3>
                  <p style={{ margin: '4px 0 0 0', opacity: 0.85, fontSize: '0.88rem' }}>{selectedUserMetrics.email}</p>
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem' }}>
                  <div style={{ background: 'rgba(255, 255, 255, 0.15)', padding: '6px 14px', borderRadius: '8px', backdropFilter: 'blur(4px)' }}>
                    <strong>Team:</strong> {officeBiData.find(r => String(r.user_id) === String(biSelectedUser))?.team_name || 'N/A'}
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.15)', padding: '6px 14px', borderRadius: '8px', backdropFilter: 'blur(4px)' }}>
                    <strong>Client:</strong> {officeBiData.find(r => String(r.user_id) === String(biSelectedUser))?.client_name || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Personal Metric Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Target Met Compliance</span>
                  <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1e293b' }}>{selectedUserMetrics.targetMetPct}%</h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Weeks meeting 3-day target ({selectedUserMetrics.weeksMetTarget} / {selectedUserMetrics.weeksActive} wks)</p>
                </div>

                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Total Days Attended</span>
                  <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1e293b' }}>{selectedUserMetrics.totalCheckins} days</h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Office days checked-in</p>
                </div>

                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Plan Compliance</span>
                  <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1e293b' }}>{selectedUserMetrics.complianceRate}%</h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Checked in {selectedUserMetrics.plannedAndAttended} of {selectedUserMetrics.totalPlanned} planned days</p>
                </div>

                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>No-Shows / Drop-Ins</span>
                  <h3 style={{ margin: '8px 0 2px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1e293b' }}>{selectedUserMetrics.noShows} / {selectedUserMetrics.dropIns}</h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>{selectedUserMetrics.noShows} missed, {selectedUserMetrics.dropIns} drop-ins</p>
                </div>
              </div>

              {/* Compact Annual Grid / Heatmap */}
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', overflowX: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', minWidth: '700px' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={18} style={{ color: '#667eea' }} />
                    Annual Attendance Grid
                  </h4>
                  
                  {/* Heatmap Year selection */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', borderRadius: '999px', padding: '4px 6px' }}>
                    <button
                      type="button"
                      onClick={() => setBiYear(y => y - 1)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', color: '#64748b' }}
                      title="Previous year"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span style={{ minWidth: '45px', textAlign: 'center', fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>{biYear}</span>
                    <button
                      type="button"
                      onClick={() => setBiYear(y => y + 1)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', color: '#64748b' }}
                      title="Next year"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: '750px' }}>
                  {/* First half */}
                  <div>
                    <h5 style={{ margin: '0 0 10px', fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}>Jan — Jun</h5>
                    <BiHeatmapGrid cells={cellsH1} monthPositions={monthPositionsH1} rowsMap={selectedUserRowsMap} today={today} />
                  </div>
                  
                  {/* Second half */}
                  <div>
                    <h5 style={{ margin: '0 0 10px', fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}>Jul — Dec</h5>
                    <BiHeatmapGrid cells={cellsH2} monthPositions={monthPositionsH2} rowsMap={selectedUserRowsMap} today={today} />
                  </div>
                </div>

                {/* Swatches Legend */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '0.78rem', color: '#64748b', marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px', minWidth: '700px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', background: BI_COLORS.EMPTY, borderRadius: '2px', border: '1px solid #cbd5e1' }} />
                    <span>Empty</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', background: BI_COLORS.FUTURE_PLANNED, borderRadius: '2px' }} />
                    <span>Planned</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', background: BI_COLORS.PLANNED, borderRadius: '2px' }} />
                    <span>No-show (Planned, Absent)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', background: BI_COLORS.ATTENDED, borderRadius: '2px' }} />
                    <span>Attended (Planned + Present)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', background: BI_COLORS.DROPIN, borderRadius: '2px' }} />
                    <span>Drop-In (Present, Unplanned)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', background: 'transparent', border: '2px solid ' + BI_COLORS.TODAY, borderRadius: '2px' }} />
                    <span>Today</span>
                  </div>
                </div>
              </div>

              {/* Recent Activity Log Table */}
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Recent Activity Log (Last 10 Days)</h4>
                
                {selectedUserMetrics.recentLogs.length === 0 ? (
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>No attendance activity logged for this user.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, color: '#475569', textAlign: 'left' }}>
                        <th style={{ padding: '10px 12px' }}>Date</th>
                        <th style={{ padding: '10px 12px' }}>Planned</th>
                        <th style={{ padding: '10px 12px' }}>Check-in Status</th>
                        <th style={{ padding: '10px 12px' }}>Check-in Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUserMetrics.recentLogs.map((row) => {
                        const isAttended = !!row.checked_in_at;
                        const isPlanned = !!row.planned;
                        let statusVal = 'Absent';
                        let statusColorVal = '#64748b';
                        let statusBgVal = '#f1f5f9';
                        
                        if (isAttended) {
                          if (isPlanned) {
                            statusVal = 'Attended';
                            statusColorVal = '#15803d';
                            statusBgVal = '#dcfce7';
                          } else {
                            statusVal = 'Drop-In';
                            statusColorVal = '#c2410c';
                            statusBgVal = '#ffedd5';
                          }
                        } else if (isPlanned) {
                          statusVal = 'No-Show';
                          statusColorVal = '#b91c1c';
                          statusBgVal = '#fee2e2';
                        }

                        return (
                          <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>
                              {new Date(row.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{
                                padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600,
                                background: isPlanned ? '#e0e7ff' : '#f1f5f9',
                                color: isPlanned ? '#4f46e5' : '#64748b'
                              }}>{isPlanned ? 'Yes' : 'No'}</span>
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{
                                padding: '4px 8px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600,
                                background: statusBgVal,
                                color: statusColorVal
                              }}>{statusVal}</span>
                            </td>
                            <td style={{ padding: '10px 12px', color: '#475569' }}>
                              {isAttended ? formatTime(row.checked_in_at) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '40px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b' }}>
              <Users size={48} style={{ color: '#94a3b8', marginBottom: '16px' }} />
              <h4 style={{ margin: '0 0 8px 0', color: '#1e293b', fontWeight: 600 }}>No Person Selected</h4>
              <p style={{ margin: 0, fontSize: '0.88rem' }}>Please select a person from the dropdown above to view their calendar attendance and details.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Sub-component: Heatmap Grid
const BI_SQUARE = 12;
const BI_GAP = 2;
function BiHeatmapGrid({ cells, monthPositions, rowsMap, today }) {
  const colCount = cells.length > 0 ? cells[cells.length - 1].col + 1 : 0;
  const gridWidth = colCount * BI_SQUARE + (colCount - 1) * BI_GAP;

  return (
    <div style={{ position: 'relative', paddingLeft: '34px', paddingTop: '16px', width: gridWidth + 34 }}>
      {/* Month Labels */}
      <div style={{ position: 'absolute', top: 0, left: '34px', height: '14px', width: gridWidth }}>
        {monthPositions.map(({ month, col }) => (
          <span
            key={month}
            style={{
              position: 'absolute',
              left: col * (BI_SQUARE + BI_GAP),
              fontSize: '0.65rem',
              color: '#64748b',
              fontWeight: 600
            }}
          >
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month]}
          </span>
        ))}
      </div>

      {/* Weekday labels */}
      <div style={{ position: 'absolute', left: 0, top: '16px', width: '30px' }}>
        {['Mon', 'Wed', 'Fri'].map((lbl, idx) => (
          <div
            key={lbl}
            style={{
              position: 'absolute',
              top: (idx * 2) * (BI_SQUARE + BI_GAP),
              fontSize: '0.62rem',
              color: '#94a3b8',
              fontWeight: 600,
              height: BI_SQUARE,
              lineHeight: `${BI_SQUARE}px`
            }}
          >
            {lbl}
          </div>
        ))}
      </div>

      {/* Grid Canvas */}
      <div style={{ position: 'relative', height: 7 * BI_SQUARE + 6 * BI_GAP, width: gridWidth }}>
        {cells.map((c) => {
          if (c.isOutOfYear) return null;
          
          const x = c.col * (BI_SQUARE + BI_GAP);
          const y = c.row * (BI_SQUARE + BI_GAP);
          const isToday = c.date === today;
          
          let color = BI_COLORS.EMPTY;
          let tooltip = c.date ? `${c.date}: Empty` : '';
          
          if (c.date && rowsMap.has(c.date)) {
            const row = rowsMap.get(c.date);
            const attended = !!row.checked_in_at;
            const planned = !!row.planned;
            
            if (attended) {
              if (planned) {
                color = BI_COLORS.ATTENDED;
                tooltip = `${c.date}: Checked in (Planned)`;
              } else {
                color = BI_COLORS.DROPIN;
                tooltip = `${c.date}: Checked in (Drop-In)`;
              }
            } else if (planned) {
              const isPast = c.date < today;
              color = isPast ? BI_COLORS.PLANNED : BI_COLORS.FUTURE_PLANNED;
              tooltip = isPast ? `${c.date}: Planned (Absent)` : `${c.date}: Planned`;
            }
          }

          return (
            <div
              key={c._jsDate.getTime()}
              title={tooltip}
              style={{
                position: 'absolute',
                left: x,
                top: y,
                width: BI_SQUARE,
                height: BI_SQUARE,
                background: color,
                borderRadius: '2px',
                border: isToday ? `2px solid ${BI_COLORS.TODAY}` : 'none',
                boxSizing: 'border-box'
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export default AdminPanel;
